import { createSlice } from "@reduxjs/toolkit";

// Import de tous les thunks
import {
  fetchAllCards,
  fetchCardById,
  createCardByPatient,
  createCardByDoctor,
  updateCard,
  deleteCard,
  fetchStatistics,
  fetchGlobalStats,
  fetchUnverifiedCards,
  fetchExpiringCards,
  fetchExpiredCards,
  verifyCardByIdentifiant,
  searchByName,
  searchByCin,
  fetchCardsByPatient,
  fetchDependentsByPatient,
  fetchCardsByCreator,
  addDependent,
  fetchDependentsByCard,
  fetchDependentById,
  updateDependent,
  deleteDependent,
  createMedicalAct,
  fetchActsByPatient,
  deleteMedicalAct,
  fetchCardsByDoctorPatient,
} from "../thunks/cnssThunks";

// ─── État initial ────────────────────────────────────────────────────────────
const initialState = {
  // Cartes
  cards: [],
  currentCard: null,

  // Recherche
  searchResults: [],
  verificationResult: null,

  doctorPatientCards: [],

  // Listes filtrées
  unverifiedCards: [],
  expiringCards: [],
  expiredCards: [],

  // Dépendants
  dependents: [],       // dépendants de la carte courante
  currentDependent: null,
  allDependents: [],    // tous les dépendants d'un patient (cross-cartes)

  // Actes médicaux (Bulletin de Soins)
  medicalActs: [],      // actes du patient courant

  // Statistiques
  statistics: null,
  globalStats: null,

  // UI / États de chargement
  loading: {
    cards: false,
    card: false,
    create: false,
    update: false,
    delete: false,
    statistics: false,
    unverified: false,
    expiring: false,
    expired: false,
    search: false,
    verify: false,
    dependents: false,
    dependent: false,
    addDependent: false,
    updateDependent: false,
    deleteDependent: false,
    medicalActs: false,
    createAct: false,
    deleteAct: false,
    doctorPatientCards: false,
  },
  error: null,
  success: null,
};

// ─── Helpers internes ────────────────────────────────────────────────────────
const setLoading = (key) => (state) => {
  state.loading[key] = true;
  state.error = null;
  state.success = null;
};

const setError = (key) => (state, action) => {
  state.loading[key] = false;
  state.error = action.payload;
};

// ─── Slice ───────────────────────────────────────────────────────────────────
const cnssSlice = createSlice({
  name: "cnss",
  initialState,

  reducers: {
    clearError: (state) => { state.error = null; },
    clearSuccess: (state) => { state.success = null; },
    clearCurrentCard: (state) => { state.currentCard = null; },
    clearCurrentDependent: (state) => { state.currentDependent = null; },
    clearVerification: (state) => { state.verificationResult = null; },
    clearSearchResults: (state) => { state.searchResults = []; },
    clearMedicalActs: (state) => { state.medicalActs = []; },
    resetCnssState: () => initialState,
  },

  extraReducers: (builder) => {

    // ── fetchCardsByDoctorPatient ───────────────────────────────
builder
  .addCase(fetchCardsByDoctorPatient.pending, setLoading("doctorPatientCards"))
  .addCase(fetchCardsByDoctorPatient.fulfilled, (state, { payload }) => {
    state.loading.doctorPatientCards = false;
    state.doctorPatientCards = Array.isArray(payload) ? payload : [];
  })
  .addCase(fetchCardsByDoctorPatient.rejected, setError("doctorPatientCards"));

    // ── fetchAllCards ────────────────────────────────────────
    builder
      .addCase(fetchAllCards.pending, setLoading("cards"))
      .addCase(fetchAllCards.fulfilled, (state, { payload }) => {
        state.loading.cards = false;
        state.cards = payload;
      })
      .addCase(fetchAllCards.rejected, setError("cards"));

    // ── fetchCardById ────────────────────────────────────────
    builder
      .addCase(fetchCardById.pending, setLoading("card"))
      .addCase(fetchCardById.fulfilled, (state, { payload }) => {
        state.loading.card = false;
        state.currentCard = payload;
      })
      .addCase(fetchCardById.rejected, setError("card"));

    // ── createCardByPatient ──────────────────────────────────
    builder
      .addCase(createCardByPatient.pending, setLoading("create"))
      .addCase(createCardByPatient.fulfilled, (state, { payload }) => {
        state.loading.create = false;
        state.cards.unshift(payload);
        state.currentCard = payload; // ✅ très important
        state.success = "Carte CNSS créée avec succès";
        })
      .addCase(createCardByPatient.rejected, setError("create"));

    // ── createCardByDoctor ───────────────────────────────────
    builder
      .addCase(createCardByDoctor.pending, setLoading("create"))
      .addCase(createCardByDoctor.fulfilled, (state, { payload }) => {
        state.loading.create = false;
        state.cards.unshift(payload);
        state.currentCard = payload; // ✅ très important
        state.success = "Carte CNSS créée pour le patient";
        })
      .addCase(createCardByDoctor.rejected, setError("create"));

    // ── updateCard ───────────────────────────────────────────
    builder
      .addCase(updateCard.pending, setLoading("update"))
      .addCase(updateCard.fulfilled, (state, { payload }) => {
        state.loading.update = false;
        state.cards = state.cards.map((c) =>
          c.id === payload.id ? payload : c
        );
        if (state.currentCard?.id === payload.id) {
          state.currentCard = payload;
        }
        state.success = "Carte mise à jour";
      })
      .addCase(updateCard.rejected, setError("update"));

    // ── deleteCard ───────────────────────────────────────────
    builder
      .addCase(deleteCard.pending, setLoading("delete"))
      .addCase(deleteCard.fulfilled, (state, { payload }) => {
        state.loading.delete = false;
        state.cards = state.cards.filter((c) => c.id !== payload);
        if (state.currentCard?.id === payload) state.currentCard = null;
        state.success = "Carte supprimée";
      })
      .addCase(deleteCard.rejected, setError("delete"));

    // ── fetchStatistics ──────────────────────────────────────
    builder
      .addCase(fetchStatistics.pending, setLoading("statistics"))
      .addCase(fetchStatistics.fulfilled, (state, { payload }) => {
        state.loading.statistics = false;
        state.statistics = payload;
      })
      .addCase(fetchStatistics.rejected, setError("statistics"));

    // ── fetchGlobalStats ─────────────────────────────────────
    builder
      .addCase(fetchGlobalStats.pending, setLoading("statistics"))
      .addCase(fetchGlobalStats.fulfilled, (state, { payload }) => {
        state.loading.statistics = false;
        state.globalStats = payload;
      })
      .addCase(fetchGlobalStats.rejected, setError("statistics"));

    // ── fetchUnverifiedCards ─────────────────────────────────
    builder
      .addCase(fetchUnverifiedCards.pending, setLoading("unverified"))
      .addCase(fetchUnverifiedCards.fulfilled, (state, { payload }) => {
        state.loading.unverified = false;
        state.unverifiedCards = payload;
      })
      .addCase(fetchUnverifiedCards.rejected, setError("unverified"));

    // ── fetchExpiringCards ───────────────────────────────────
    builder
      .addCase(fetchExpiringCards.pending, setLoading("expiring"))
      .addCase(fetchExpiringCards.fulfilled, (state, { payload }) => {
        state.loading.expiring = false;
        state.expiringCards = payload;
      })
      .addCase(fetchExpiringCards.rejected, setError("expiring"));

    // ── fetchExpiredCards ────────────────────────────────────
    builder
      .addCase(fetchExpiredCards.pending, setLoading("expired"))
      .addCase(fetchExpiredCards.fulfilled, (state, { payload }) => {
        state.loading.expired = false;
        state.expiredCards = payload;
      })
      .addCase(fetchExpiredCards.rejected, setError("expired"));

    // ── verifyCardByIdentifiant ──────────────────────────────
    builder
      .addCase(verifyCardByIdentifiant.pending, setLoading("verify"))
      .addCase(verifyCardByIdentifiant.fulfilled, (state, { payload }) => {
        state.loading.verify = false;
        state.verificationResult = payload;
      })
      .addCase(verifyCardByIdentifiant.rejected, setError("verify"));

    // ── searchByName ─────────────────────────────────────────
    builder
      .addCase(searchByName.pending, setLoading("search"))
      .addCase(searchByName.fulfilled, (state, { payload }) => {
        state.loading.search = false;
        state.searchResults = payload;
      })
      .addCase(searchByName.rejected, setError("search"));

    // ── searchByCin ──────────────────────────────────────────
    builder
      .addCase(searchByCin.pending, setLoading("search"))
      .addCase(searchByCin.fulfilled, (state, { payload }) => {
        state.loading.search = false;
        state.searchResults = payload;
      })
      .addCase(searchByCin.rejected, setError("search"));

    // ── fetchCardsByPatient ──────────────────────────────────
    builder
      .addCase(fetchCardsByPatient.pending, setLoading("cards"))
      .addCase(fetchCardsByPatient.fulfilled, (state, { payload }) => {
        state.loading.cards = false;
        state.cards = payload;
      })
      .addCase(fetchCardsByPatient.rejected, setError("cards"));

    // ── fetchDependentsByPatient ─────────────────────────────
    builder
      .addCase(fetchDependentsByPatient.pending, setLoading("dependents"))
      .addCase(fetchDependentsByPatient.fulfilled, (state, { payload }) => {
        state.loading.dependents = false;
        state.allDependents = payload;
      })
      .addCase(fetchDependentsByPatient.rejected, setError("dependents"));

    // ── fetchCardsByCreator ──────────────────────────────────
    builder
      .addCase(fetchCardsByCreator.pending, setLoading("cards"))
      .addCase(fetchCardsByCreator.fulfilled, (state, { payload }) => {
        state.loading.cards = false;
        state.cards = payload;
      })
      .addCase(fetchCardsByCreator.rejected, setError("cards"));

    // ── addDependent ─────────────────────────────────────────
    builder
      .addCase(addDependent.pending, setLoading("addDependent"))
      .addCase(addDependent.fulfilled, (state, { payload }) => {
        state.loading.addDependent = false;
        state.dependents.push(payload);
        state.success = "Dépendant ajouté avec succès";
      })
      .addCase(addDependent.rejected, setError("addDependent"));

    // ── fetchDependentsByCard ────────────────────────────────
    builder
      .addCase(fetchDependentsByCard.pending, setLoading("dependents"))
     .addCase(fetchDependentsByCard.fulfilled, (state, { payload }) => {
        state.loading.dependents = false;
        // Le service renvoie { card, dependents, … } OU un tableau direct
        state.dependents = Array.isArray(payload)
          ? payload
          : payload.dependents ?? [];
      })
      .addCase(fetchDependentsByCard.rejected, setError("dependents"));

    // ── fetchDependentById ───────────────────────────────────
    builder
      .addCase(fetchDependentById.pending, setLoading("dependent"))
      .addCase(fetchDependentById.fulfilled, (state, { payload }) => {
        state.loading.dependent = false;
        state.currentDependent = payload;
      })
      .addCase(fetchDependentById.rejected, setError("dependent"));

    // ── updateDependent ──────────────────────────────────────
    builder
      .addCase(updateDependent.pending, setLoading("updateDependent"))
      .addCase(updateDependent.fulfilled, (state, { payload }) => {
        state.loading.updateDependent = false;
        state.dependents = state.dependents.map((d) =>
          d.id === payload.id ? payload : d
        );
        if (state.currentDependent?.id === payload.id) {
          state.currentDependent = payload;
        }
        state.success = "Dépendant mis à jour";
      })
      .addCase(updateDependent.rejected, setError("updateDependent"));

    // ── deleteDependent ──────────────────────────────────────
    builder
      .addCase(deleteDependent.pending, setLoading("deleteDependent"))
      .addCase(deleteDependent.fulfilled, (state, { payload }) => {
        state.loading.deleteDependent = false;
        state.dependents = state.dependents.filter(
          (d) => d.id !== payload.dependentId
        );
        state.success = "Dépendant supprimé";
      })
      .addCase(deleteDependent.rejected, setError("deleteDependent"));

    // ════════════════════════════════════════════════════════
    //  ACTES MÉDICAUX - BULLETIN DE SOINS
    // ════════════════════════════════════════════════════════

    // ── createMedicalAct ─────────────────────────────────────
    builder
      .addCase(createMedicalAct.pending, setLoading("createAct"))
      .addCase(createMedicalAct.fulfilled, (state, { payload }) => {
        state.loading.createAct = false;
        state.medicalActs.unshift(payload);
        state.success = "Acte médical enregistré avec succès";
      })
      .addCase(createMedicalAct.rejected, setError("createAct"));

    // ── fetchActsByPatient ────────────────────────────────────
    builder
      .addCase(fetchActsByPatient.pending, setLoading("medicalActs"))
      .addCase(fetchActsByPatient.fulfilled, (state, { payload }) => {
        state.loading.medicalActs = false;
        state.medicalActs = payload;
      })
      .addCase(fetchActsByPatient.rejected, setError("medicalActs"));

    // ── deleteMedicalAct ──────────────────────────────────────
    builder
      .addCase(deleteMedicalAct.pending, setLoading("deleteAct"))
      .addCase(deleteMedicalAct.fulfilled, (state, { payload }) => {
        state.loading.deleteAct = false;
        state.medicalActs = state.medicalActs.filter((a) => a.id !== payload);
        state.success = "Acte médical supprimé";
      })
      .addCase(deleteMedicalAct.rejected, setError("deleteAct"));
  },
});

export const {
  clearError,
  clearSuccess,
  clearCurrentCard,
  clearCurrentDependent,
  clearVerification,
  clearSearchResults,
  clearMedicalActs,
  resetCnssState,
} = cnssSlice.actions;

export default cnssSlice.reducer;